import type { FC } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Locale } from "@/declarations/models/Locale";
import Api from "../../services/Api";
import BreadcrumbNode from "../../components/BreadcrumbNode";
import CreateOrEditRoutes from "./CreateOrEditRoutes";
import { AdminResourcePath } from "@/declarations/AdminResourcePath";
import type { DataListRow } from "@/components/DataList/DataListRow";
import { schema } from "@/declarations/schemas/locale/schema";
import { uiSchema } from "@/declarations/schemas/locale/uiSchema";
import type { DataListProps } from "@/components/DataList/DataList";
import type { CreateOrEditProps } from "@/components/CreateOrEdit";
import type { RequestContext } from "@/declarations/RequestContext";
import { ViewType } from "@/components/DataList/ListHeader/ViewTypeSelector";
import type { SearchProp } from "@/components/DataList/ListHeader/DataListHeader";
import { useDebounce } from "@/hooks/useDebounce";
import { useSortAndFilterState } from "@/hooks/useSortAndFilterState";

const mapperFn = (localeItem: Locale): DataListRow => ({
  key: String(localeItem.id),
  title: localeItem.name,
  chipContent: localeItem.language_code,
  infoText: `ID: ${localeItem.id}`,
  updatedAt: localeItem.updated_at,
  updatedBy: localeItem.updated_by,
});

const getLocale: CreateOrEditProps<Locale>["getFormData"] = (id: number): RequestContext<Locale> =>
  Api.getOneLocale(id);

const updateLocale: CreateOrEditProps<Locale>["onSubmit"] = (locale: Locale): RequestContext<Locale> =>
  Api.updateLocale(locale);

const createLocale: CreateOrEditProps<Locale>["onSubmit"] = (locale: Locale): RequestContext<Locale> =>
  Api.createLocale(locale);

export const LocaleView: FC = () => {
  const { t } = useTranslation("common");

  const { setSearchParameters, sortBy, sortAscending, initialSortOption } = useSortAndFilterState({
    defaultSortBy: "name",
  });
  const [lastFetchedTimestamp, setLastFetchedTimestamp] = useState(Date.now());
  const [searchInput, setSearchInput] = useState<string>("");
  const [searchTerms, setSearchTerms] = useState<string>("");

  const debouncedSetSearchTerms = useDebounce<string>(500, (t) => {
    setSearchTerms(t || "");
    setLastFetchedTimestamp(Date.now());
  });

  useEffect(() => {
    if (searchInput !== searchTerms) debouncedSetSearchTerms(searchInput);
  }, [debouncedSetSearchTerms, searchInput, searchTerms]);

  const handleOnItemsChanged = async (sortProp?: string, sortDirection?: string) => {
    setSearchParameters(sortProp, sortDirection);
    setLastFetchedTimestamp(Date.now());
  };

  const getLocales: DataListProps<Locale>["getItems"] = (page, page_size) =>
    Api.getAllLocales({
      page,
      page_size,
      sort_by: sortBy,
      order_asc: sortAscending,
      search: searchTerms,
    }).fetchDirect({
      page,
      page_size,
      count: 0,
      items: [],
      total_count: 0,
    });

  return (
    <>
      <BreadcrumbNode label={`adminResourcePath.${AdminResourcePath.LOCALE}`} />
      <CreateOrEditRoutes
        schema={schema}
        uiSchema={uiSchema}
        goBackOnSubmit
        createFormData={createLocale}
        updateFormData={updateLocale}
        getFormData={getLocale}
        listProps={{
          listTitle: t("schemaTypes.locale_plural"),
          mapperFn,
          getItems: getLocales,
          hideImage: true,
          defaultViewType: ViewType.LIST,
          disableViewTypeSelection: true,
          handleOnItemsChanged,
          externalDataChanged: lastFetchedTimestamp,
          initialSortOption,
          resetPageDeps: [searchTerms],
          searchProp: {
            query: searchInput,
            updateQuery: setSearchInput,
            placeholder: t("search.inputLabel"),
          } as SearchProp,
        }}
      />
    </>
  );
};

export default LocaleView;
